import { forwardRef, memo } from 'react';
import type { ComponentPropsWithoutRef, MouseEventHandler, ReactElement, Ref } from 'react';

import { cx } from '@/utils/cx';

import { useScrollContext } from '../HeaderContext';

import styles from './HeaderTitleGroup.module.scss';

import type { VisibleOn } from '../types';

export interface HeaderTitleGroupProps extends Omit<ComponentPropsWithoutRef<'div'>, 'onClick'> {
  /** 스크롤 시에만 표시 (CSS class 토글, DOM 항상 유지) */
  showOnScroll?: boolean;
  /** 특정 디바이스에서만 노출. 미지정 = 항상 노출 */
  visibleOn?: VisibleOn;
  /**
   * 지정 시 **그룹 전체**가 `<button type="button">` 으로 렌더된다 (미지정 시 `<div>`).
   *
   * 파트별 핸들러를 두지 않는 이유는 컴포넌트 JSDoc 참조.
   * element 타입은 렌더 분기에 따라 달라지므로 `HTMLElement` 로 받는다.
   */
  onClick?: MouseEventHandler<HTMLElement>;
}

/**
 * title + subTitle 2줄 타이틀 블록.
 *
 * `Header.Title` 과 공존한다 — 선택 기준은 **타이포그래피를 누가 소유하는가** 다.
 *
 * - `Header.Title`: 타이포를 호스트가 소유하는 자유 슬롯. 단일 줄. 기존 사용처 유지.
 * - `Header.TitleGroup`: 디자인 스펙(14_bold / 12_regular)을 라이브러리가 소유하는 2줄 블록.
 *
 * # 클릭은 그룹이 단독 소유한다
 *
 * `onClick` 을 주면 **그룹 전체**가 `<button>` 으로 렌더된다 (터치 영역 = 타이틀 영역 전체).
 * 디자인상 title 줄과 subTitle 줄이 동시에 클릭 가능한 상태는 존재하지 않으므로,
 * 파트별 `onClick` 은 의도적으로 제공하지 않는다. 파트의 `trailingIcon` 은 장식일 뿐
 * 자체 핸들러를 갖지 않는다 — "둘 다 클릭 가능" 이 타입 수준에서 표현 불가능해진다.
 *
 * 접근성 이름은 내부 텍스트 자동 결합이 기본이다 (날짜/인원 같은 subTitle 값도 버튼의
 * 현재 상태로 읽히는 편이 유용). 더 나은 문구가 필요하면 `aria-label` 로 덮는다.
 *
 * # PC 스펙
 *
 * 디자인이 모바일(360×52) 프레임만 제공하므로 PC 전용 타이포/간격은 **도입하지 않았다**.
 * 근거 없는 값을 기본값으로 굳히면 나중에 스펙이 나왔을 때 BC 를 깨야 한다.
 * PC 에서 다르게 보여야 한다면 `visibleOn` 으로 호스트가 분기한다.
 *
 * @example
 * ```tsx
 * <Header.TitleGroup visibleOn="mobile" onClick={openSearchSheet} aria-label="검색 조건 변경">
 *   <Header.TitleGroup.Title trailingIcon={<IcArrowmoreFill />}>도쿄</Header.TitleGroup.Title>
 *   <Header.TitleGroup.SubTitle trailingIcon={<IcEditFill />}>
 *     9.18 ~ 9.20
 *     <Header.TitleGroup.Divider />
 *     성인 2, 소아 1
 *   </Header.TitleGroup.SubTitle>
 * </Header.TitleGroup>
 * ```
 */
export const HeaderTitleGroup = memo(
  forwardRef<HTMLDivElement, HeaderTitleGroupProps>(
    ({ children, className, visibleOn, showOnScroll, onClick, ...rest }, ref) => {
      const { isScrolled } = useScrollContext();
      const isHidden = showOnScroll && !isScrolled;
      const isInteractive = onClick !== undefined;

      // `data-core-ui-header-slot="title"` 은 HeaderLeft 의 spacing 매트릭스(로고 ↔ 텍스트 inset)
      // 진입 키다. 이 attribute 는 컴포넌트 정체가 아니라 "텍스트 콘텐츠가 시작되는 위치" 라는
      // 시각적 역할을 뜻하므로 (Header.Content 도 동일 취급) TitleGroup 이 그대로 재사용한다.
      const shared = {
        'aria-hidden': isHidden || undefined,
        className: cx(styles.group, isInteractive && styles.interactive, isHidden && styles.hidden, className),
        'data-core-ui-header-slot': 'title',
        'data-core-ui-hidden': isHidden || undefined,
        'data-core-ui-visible-on': visibleOn,
      };

      if (isInteractive) {
        return (
          <button
            {...shared}
            onClick={onClick}
            ref={ref as unknown as Ref<HTMLButtonElement>}
            type="button"
            {...(rest as ComponentPropsWithoutRef<'button'>)}
          >
            {children}
          </button>
        );
      }

      return (
        <div {...shared} ref={ref} {...rest}>
          {children}
        </div>
      );
    },
  ),
);

HeaderTitleGroup.displayName = 'Header.TitleGroup';

export interface HeaderTitleGroupTitleProps extends ComponentPropsWithoutRef<'span'> {
  /**
   * 텍스트 뒤에 따라붙는 16×16 아이콘 (예: `ic_arrowmore_fill`).
   * 우측 끝 고정이 아니라 텍스트 폭을 따라 이동한다.
   *
   * asset 은 호스트가 주입한다 — core-ui 는 runtime dependency 가 없어 아이콘을 내장하지 않는다.
   * `Header.Action` 의 `icon` prop 과 같은 계약이다.
   */
  trailingIcon?: ReactElement;
}

export const HeaderTitleGroupTitle = memo(
  forwardRef<HTMLSpanElement, HeaderTitleGroupTitleProps>(({ children, className, trailingIcon, ...rest }, ref) => (
    <span className={cx(styles.title, className)} ref={ref} {...rest}>
      <span className={styles.text}>{children}</span>
      {trailingIcon ? (
        <span aria-hidden className={cx(styles.icon, styles.titleIcon)}>
          {trailingIcon}
        </span>
      ) : null}
    </span>
  )),
);

HeaderTitleGroupTitle.displayName = 'Header.TitleGroup.Title';

export interface HeaderTitleGroupSubTitleProps extends ComponentPropsWithoutRef<'span'> {
  /**
   * 텍스트 뒤에 따라붙는 14×14 아이콘 (예: `ic_arrowmore_fill` / `ic_edit_fill`).
   * 디자인상 아이콘 종류가 화면마다 달라지므로 boolean 스위치가 아닌 노드로 받는다.
   */
  trailingIcon?: ReactElement;
}

export const HeaderTitleGroupSubTitle = memo(
  forwardRef<HTMLSpanElement, HeaderTitleGroupSubTitleProps>(({ children, className, trailingIcon, ...rest }, ref) => (
    <span className={cx(styles.subTitle, className)} ref={ref} {...rest}>
      <span className={styles.text}>{children}</span>
      {trailingIcon ? (
        <span aria-hidden className={cx(styles.icon, styles.subTitleIcon)}>
          {trailingIcon}
        </span>
      ) : null}
    </span>
  )),
);

HeaderTitleGroupSubTitle.displayName = 'Header.TitleGroup.SubTitle';

/**
 * subTitle 세그먼트 구분자 (1×10, `$line-neutral-weak2`, 양옆 4px).
 *
 * 삽입 여부는 호스트가 결정한다 — 디자인이 구분자를 Divider 로 그리는 화면과
 * 텍스트 `·` 로 이어붙이는 화면을 모두 갖고 있어, 라이브러리가 자동 삽입하면
 * 후자를 표현할 수 없거나 두 가지 렌더가 뒤섞인다.
 */
export const HeaderTitleGroupDivider = memo(
  forwardRef<HTMLSpanElement, ComponentPropsWithoutRef<'span'>>(({ className, ...rest }, ref) => (
    <span aria-hidden className={cx(styles.divider, className)} ref={ref} {...rest} />
  )),
);

HeaderTitleGroupDivider.displayName = 'Header.TitleGroup.Divider';
